import { logger } from '@/utils/logger';
/**
 * Security Monitoring and Event Logging
 * Enhanced audit logging for security events and compliance
 */

import { supabase } from '@/integrations/supabase/client';

interface SecurityEvent {
  event_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  user_id?: string;
  organization_id?: string;
  ip_address?: string;
  user_agent?: string;
  details: Record<string, any>;
  source: 'client' | 'edge-function' | 'rls-policy' | 'auth-flow';
}

interface RateLimitViolation {
  endpoint: string;
  user_id?: string;
  ip_address?: string;
  attempts: number;
  window_ms: number;
  blocked_until: Date;
}

interface AuthSecurityEvent {
  event_type: 'login_attempt' | 'login_failure' | 'logout' | 'password_change' | 'email_change' | 'mfa_setup' | 'mfa_failure' | 'session_timeout';
  user_id?: string;
  email?: string;
  ip_address?: string;
  success: boolean;
  failure_reason?: string;
  device_info?: string;
}

interface DataAccessEvent {
  event_type: 'data_export' | 'bulk_download' | 'sensitive_query' | 'policy_violation' | 'unauthorized_access';
  user_id: string;
  organization_id: string;
  resource_type: string;
  resource_id?: string;
  action: string;
  granted: boolean;
  reason?: string;
}

class SecurityMonitor {
  /**
   * Log general security events
   */
  async logSecurityEvent(event: SecurityEvent): Promise<void> {
    try {
      await supabase.rpc('create_audit_log', {
        _organization_id: event.organization_id,
        _user_id: event.user_id,
        _action: event.event_type,
        _entity_type: 'security_event',
        _metadata: {
          severity: event.severity,
          ip_address: event.ip_address,
          user_agent: event.user_agent,
          source: event.source,
          details: event.details,
          timestamp: new Date().toISOString()
        }
      });

      // Also log to console for immediate monitoring
      logger.debug(`[SECURITY-${event.severity.toUpperCase()}]`, {
        event_type: event.event_type,
        user_id: event.user_id,
        organization_id: event.organization_id,
        source: event.source,
        details: event.details
      });
    } catch (error) {
      logger.error('Failed to log security event:', error);
    }
  }

  /**
   * Log authentication security events
   */
  async logAuthEvent(event: AuthSecurityEvent): Promise<void> {
    await this.logSecurityEvent({
      event_type: `auth_${event.event_type}`,
      severity: event.success ? 'low' : 'medium',
      user_id: event.user_id,
      ip_address: event.ip_address,
      source: 'auth-flow',
      details: {
        email: event.email,
        success: event.success,
        failure_reason: event.failure_reason,
        device_info: event.device_info
      }
    });
  }

  /**
   * Log data access events
   */
  async logDataAccessEvent(event: DataAccessEvent): Promise<void> {
    await this.logSecurityEvent({
      event_type: `data_${event.event_type}`,
      severity: event.granted ? 'low' : 'high',
      user_id: event.user_id,
      organization_id: event.organization_id,
      source: 'rls-policy',
      details: {
        resource_type: event.resource_type,
        resource_id: event.resource_id,
        action: event.action,
        granted: event.granted,
        reason: event.reason
      }
    });
  }

  /**
   * Log rate limit violations
   */
  async logRateLimitViolation(violation: RateLimitViolation): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'rate_limit_violation',
      severity: 'medium',
      user_id: violation.user_id,
      ip_address: violation.ip_address,
      source: 'edge-function',
      details: {
        endpoint: violation.endpoint,
        attempts: violation.attempts,
        window_ms: violation.window_ms,
        blocked_until: violation.blocked_until.toISOString()
      }
    });
  }

  /**
   * Log suspicious activity patterns
   */
  async logSuspiciousActivity(
    pattern: string,
    details: Record<string, any>,
    severity: 'medium' | 'high' | 'critical' = 'high'
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'suspicious_activity',
      severity,
      source: 'client',
      details: {
        pattern,
        ...details
      }
    });
  }

  /**
   * Log policy violations
   */
  async logPolicyViolation(
    policy: string,
    user_id: string,
    organization_id: string,
    details: Record<string, any>
  ): Promise<void> {
    await this.logSecurityEvent({
      event_type: 'policy_violation',
      severity: 'high',
      user_id,
      organization_id,
      source: 'rls-policy',
      details: {
        policy,
        ...details
      }
    });
  }

  /**
   * Check for security anomalies (client-side detection)
   */
  detectClientAnomalies(): void {
    // Detect rapid API calls
    this.detectRapidApiCalls();
    
    // Detect unusual navigation patterns
    this.detectUnusualNavigation();
    
    // Detect potential XSS attempts in URL parameters
    this.detectXSSAttempts();
  }

  private detectRapidApiCalls(): void {
    const apiCallTimes: number[] = [];
    const originalFetch = window.fetch;
    
    window.fetch = (...args) => {
      const now = Date.now();
      apiCallTimes.push(now);
      
      // Remove calls older than 10 seconds
      const tenSecondsAgo = now - 10000;
      while (apiCallTimes.length > 0 && apiCallTimes[0] < tenSecondsAgo) {
        apiCallTimes.shift();
      }
      
      // Check if more than 50 API calls in 10 seconds
      if (apiCallTimes.length > 50) {
        this.logSuspiciousActivity('rapid_api_calls', {
          calls_count: apiCallTimes.length,
          window_seconds: 10,
          endpoint: args[0]
        });
      }
      
      return originalFetch(...args);
    };
  }

  private detectUnusualNavigation(): void {
    let navigationCount = 0;
    const startTime = Date.now();
    
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      navigationCount += entries.length;
      
      const elapsedMinutes = (Date.now() - startTime) / 60000;
      if (elapsedMinutes >= 1 && navigationCount > 30) {
        this.logSuspiciousActivity('rapid_navigation', {
          navigation_count: navigationCount,
          elapsed_minutes: elapsedMinutes
        });
      }
    });
    
    observer.observe({ entryTypes: ['navigation'] });
  }

  private detectXSSAttempts(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+=/i,
      /eval\(/i,
      /document\.cookie/i,
      /localStorage/i,
      /sessionStorage/i
    ];
    
    for (const [key, value] of urlParams.entries()) {
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(value)) {
          this.logSuspiciousActivity('potential_xss', {
            parameter: key,
            value: value.substring(0, 100), // Limit logged value length
            pattern: pattern.source,
            url: window.location.href
          }, 'critical');
          break;
        }
      }
    }
  }

  /**
   * Monitor for CSP violations
   */
  setupCSPViolationReporting(): void {
    document.addEventListener('securitypolicyviolation', (event) => {
      this.logSecurityEvent({
        event_type: 'csp_violation',
        severity: 'medium',
        source: 'client',
        details: {
          violated_directive: event.violatedDirective,
          blocked_uri: event.blockedURI,
          source_file: event.sourceFile,
          line_number: event.lineNumber,
          column_number: event.columnNumber,
          original_policy: event.originalPolicy
        }
      });
    });
  }

  /**
   * Get current user security context
   */
  async getCurrentUserContext(): Promise<{
    user_id?: string;
    organization_id?: string;
    ip_address?: string;
    user_agent?: string;
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      let organizationId: string | undefined;
      if (user?.id) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single();
        organizationId = profile?.organization_id;
      }

      return {
        user_id: user?.id,
        organization_id: organizationId,
        user_agent: navigator.userAgent,
        // Note: IP address detection requires server-side implementation
      };
    } catch (error) {
      logger.error('Failed to get user context:', error);
      return {};
    }
  }
}

export const securityMonitor = new SecurityMonitor();

// Initialize client-side security monitoring
if (typeof window !== 'undefined') {
  securityMonitor.setupCSPViolationReporting();
  securityMonitor.detectClientAnomalies();
}

// Export for use in components and services
export { SecurityMonitor };
export type { SecurityEvent, AuthSecurityEvent, DataAccessEvent, RateLimitViolation };